/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { ITestPointItem } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { ITestRunsMenu } from "Test/TestPlansHubRefresh/TestRunsMenu/TestRunsMenu.Types";
import { TestCase, TestPoint, TestPointUpdateParams } from "TestPlanCommon/Contracts/TestPlan";
import { SortOrder } from "VSSUI/Table";

export const PointsActions = {
    // initialize execute pivot
    initializeExecutePivot: () => createAction(PointsActionsTypes.initializeExecutePivot),

    // Action to invoke loading the initial test points as a part of page load
    loadInitialTestPoints: () => createAction(PointsActionsTypes.loadInitialTestPoints),

    // Action to invoke when the initial test points are fetched
    loadInitialTestPointsSucceeded: (testPoints: TestPoint[]) => createAction(PointsActionsTypes.loadInitialTestPointsSucceeded, testPoints),

    // Actions invoked when the test point data is loaded. Reload will make sure the testpoints needs to be reloaded. If reload is false , then the testpoints are appended to the state.
    loadTestPointsForSelectedSuiteSucceded: (testPoints: TestPoint[], reload?: boolean) =>
        createAction(PointsActionsTypes.loadTestPointsForSelectedSuiteSucceded, { testPoints, reload }),

    // Actions invoked to load test point for a selected suite
    loadTestPointsForSelectedSuite: (testPlanId: number, testSuiteId: number, continuationToken?: string) =>
        createAction(PointsActionsTypes.loadTestPointsForSelectedSuite, { testPlanId, testSuiteId, continuationToken }),

    // Action invoked to update test points
    updateTestPointsForSelectedSuite: (updateTestPoints: TestPointUpdateParams[], testPlanId: number, testSuiteId: number) =>
        createAction(PointsActionsTypes.updateTestPointsForSelectedSuite, { updateTestPoints, testPlanId, testSuiteId }),

    // Action invoked when the test points are loaded
    updateTestPointsForSelectedSuiteSucceded: (testPoints: TestPoint[]) =>
        createAction(PointsActionsTypes.updateTestPointsForSelectedSuiteSucceded, testPoints),

    // Action invoked to create test runs and open the test runner.
    openApplicationTestRunner: (testRunMenu: ITestRunsMenu) => createAction(PointsActionsTypes.openApplicationTestRunner, testRunMenu),

    // Action invoked to resume a test run and open the test runner.
    openApplicationTestRunnerToResumeTest: (testRunMenu: ITestRunsMenu) =>
        createAction(PointsActionsTypes.openApplicationTestRunnerToResumeTest, testRunMenu),

    // Action invoked to create test runs and open the test runner.
    openApplicationTestRunnerClick: (testRunMenu: ITestRunsMenu) => createAction(PointsActionsTypes.openApplicationTestRunnerClick, testRunMenu),

    // Action invoked to initiate the flow of resuming a test run using test runner after the option has been clicked
    openApplicationTestRunnerClickToResumeTest: (testRunMenu: ITestRunsMenu) =>
        createAction(PointsActionsTypes.openApplicationTestRunnerClickToResumeTest, testRunMenu),

    // Action invoked to run test points using web runner.
    openTestPointsRunnerClient: (testRunMenu: ITestRunsMenu) => createAction(PointsActionsTypes.openTestPointsRunnerClient, testRunMenu),

    // Action invoked to resume paused test point using web runner.
    resumeTest: (pointToResume: ITestPointItem, planId: number, suiteId: number) =>
        createAction(PointsActionsTypes.resumeTest, { pointToResume, planId, suiteId }),

    // Action invoked to refresh test points on closing web runner
    refreshTestPointsForWebRunner: (testPlanId: number, testSuiteId: number, testPointIds: number[]) =>
        createAction(PointsActionsTypes.refreshTestPointsForWebRunner, { testPlanId, testSuiteId, testPointIds }),

    // Action invoke to clear the previous value of test points. Invoked when user selects a different suite
    unloadPreviousTestPoints: () => createAction(PointsActionsTypes.unloadPreviousTestPoints),

    // Action to update filtered items in store, the selector will use this to return test points to render
    updateFilteredTestPoints: (filteredTestPoints: number[] | undefined) =>
        createAction(PointsActionsTypes.updateFilteredTestPoints, { filteredTestPoints }),

    // This action is used to close the assign tester dropdown after user assigned a tester
    updateAssignTesterDropdownState: (state: boolean) => createAction(PointsActionsTypes.updateAssignTesterDropdownState, state),

    // Action to invoke to open existing testcase via wit form
    openExistingTestCaseWitForm: (
        planId: number,
        suiteId: number,
        testCaseId: number,
        testCaseTitle: string,
        testCaseState: string,
        testCaseAssignedTo: string,
        testPoints: ITestPointItem[]
    ) =>
        createAction(PointsActionsTypes.openExistingTestCaseWitForm, {
            planId,
            suiteId,
            testCaseId,
            testCaseTitle,
            testCaseState,
            testCaseAssignedTo,
            testPoints
        }),

    // Action to invoke to update all test points for a test case when testcase is saved via wit form
    updateAllTestPointsByTestCaseId: (
        testCaseId: number,
        testCaseTitle: string,
        testCaseState: string,
        testCaseAssignedTo: string,
        testPoints: ITestPointItem[],
        columnOptions: { [id: string]: any }
    ) =>
        createAction(PointsActionsTypes.updateAllTestPointsByTestCaseId, {
            testCaseId,
            testCaseTitle,
            testCaseState,
            testCaseAssignedTo,
            testPoints,
            columnOptions
        }),

    // This action is used to open the test result for test point
    openTestResult: (pointId: number, runId: number, resultId: number) =>
        createAction(PointsActionsTypes.openTestResult, { pointId, runId, resultId }),

    // Action to update column id and sort order for sorting of table items
    updateColumnAndSortOrder: (columnId: string, proposedSortOrder: SortOrder) =>
        createAction(PointsActionsTypes.updateColumnAndSortOrder, { columnId, proposedSortOrder }),

    // Actions invoked when the test point ColumnOptions data is loaded.
    loadTestPointColumnOptionsForSelectedSuiteSucceeded: (testCases: TestCase[], reload: boolean) =>
        createAction(PointsActionsTypes.loadTestPointColumnOptionsForSelectedSuiteSucceeded, { testCases, reload }),

    // dispatched to save column widths via settings service
    saveColumnWidths: (columnAndWidthMap: { [columnId: string]: number }) =>
        createAction(PointsActionsTypes.saveColumnWidths, { columnAndWidthMap: columnAndWidthMap }),

    // dispatched to fetch column widths from setting service
    fetchColumnWidths: () => createAction(PointsActionsTypes.fetchColumnWidths, {}),

    // dispatched to open confirmation dialog if automated testpoints are run with ATR
    openAutomatedTestCasesConfirmationDialog: (testRunMenu: ITestRunsMenu) => createAction(PointsActionsTypes.openAutomatedTestCasesConfirmationDialog, testRunMenu),

    // dispatched to edit test cases
    bulkEditTestCases: (testPointIds: number[], testCaseIds: number[], planId: number, suiteId: number) => createAction(PointsActionsTypes.bulkEditTestCases, { testPointIds, testCaseIds, planId, suiteId }),

    // dispatched to filter points in our state
    filterPointsInState: () => createAction(PointsActionsTypes.filterPointsInState, {}),

    // dispatched to fetch point counts for suite tree
    fetchSuitePointCounts: () => createAction(PointsActionsTypes.fetchSuitePointCounts, {})

};

export const enum PointsActionsTypes {
    initializeExecutePivot = "Points/initializeExecutePivot",
    loadInitialTestPoints = "Points/loadInitialTestPoints",
    loadInitialTestPointsSucceeded = "Points/loadInitialTestPointsSucceeded",
    loadTestPointsForSelectedSuiteSucceded = "Points/loadTestPointsForSelectedSuiteSucceded",
    loadTestPointsForSelectedSuite = "Points/loadTestPointsForSelectedSuite",
    updateTestPointsForSelectedSuite = "Points/updateTestPointsForSelectedSuite",
    updateTestPointsForSelectedSuiteSucceded = "Points/updateTestPointsForSelectedSuiteSucceded",
    openApplicationTestRunner = "Points/openApplicationTestRunner",
    openApplicationTestRunnerToResumeTest = "Points/openApplicationTestRunnerToResumeTest",
    openApplicationTestRunnerClick = "Points/openApplicationTestRunnerClick",
    openApplicationTestRunnerClickToResumeTest = "Points/openApplicationTestRunnerClickToResumeTest",
    unloadPreviousTestPoints = "Points/unloadPreviousTestPoints",
    updateFilteredTestPoints = "Points/updateFilteredTestPoints",
    filterPointsInState = "Points/filterPointsInState",
    fetchSuitePointCounts = "Points/fetchSuitePointCounts",
    updateAssignTesterDropdownState = "Points/updateAssignTesterDropdownState",
    openExistingTestCaseWitForm = "Points/openExistingTestCaseWitForm",
    updateAllTestPointsByTestCaseId = "Points/updateAllTestPointsByTestCaseId",
    openTestPointsRunnerClient = "Points/openTestPointsRunnerClient",
    resumeTest = "Points/resumeTest",
    refreshTestPointsForWebRunner = "Points/refreshTestPointsForWebRunner",
    openTestResult = "Points/openTestResult",
    updateColumnAndSortOrder = "Points/updateColumnAndSortOrder",
    loadTestPointColumnOptionsForSelectedSuiteSucceeded = "Points/loadTestPointColumnOptionsForSelectedSuiteSucceeded",
    saveColumnWidths = "Points/saveColumnWidths",
    fetchColumnWidths = "Points/fetchColumnWidths",
    openAutomatedTestCasesConfirmationDialog = "Points/openAutomatedTestCasesConfirmationDialog",
    bulkEditTestCases = "Points/bulkEditTestCases"
}

export type PointsActions = ActionsUnion<typeof PointsActions>;
